import * as React from "react";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { GridActionsCellItem, GridToolbarContainer } from "@mui/x-data-grid";
import { default as DataGrid } from "../../../../components/LocalizedDataGrid";
import { useTranslation } from "react-i18next";
import MeteringPointEditPopUp, {
    IDENTIFIER_FORMATS,
    controllerId,
} from "./MeteringPointEditPopUp";
import { GROUP_TYPE_TO_TEXT } from "./MeteringGroupEditPopUp";
import { ApiContext } from "../../../../App";

function PointToolbar(props) {
    const { onAddClick } = props;

    const { t } = useTranslation();

    return (
        <GridToolbarContainer>
            <Button color="primary" startIcon={<AddIcon />} onClick={onAddClick}>
                {t("Add Point")}
            </Button>
        </GridToolbarContainer>
    );
}

export default function MeteringPointEditor(props) {
    const { facility } = props;

    const { t } = useTranslation();

    const [rows, setRows] = React.useState([]);
    const [controllers, setControllers] = React.useState([]);
    const [tariffs, setTariffs] = React.useState([]);
    const [groups, setGroups] = React.useState([]);
    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
    const [deleteCandidate, setDeleteCandidate] = React.useState();
    const [editPopUpOpen, setEditPopUpOpen] = React.useState(false);
    const [editPopUpData, setEditPopUpData] = React.useState({});

    const { powerSensorPointApi, controllerApi, meteringTariffApi, groupApi } =
        React.useContext(ApiContext);

    React.useEffect(() => {
        const onError = () => {
            setAlertMessage(t("Couldn't connect to server."));
            setAlertOpen(true);
        };
        powerSensorPointApi.list(facility, (data) => setRows(data), onError);
        controllerApi.list(
            facility,
            (data) =>
                setControllers(
                    data.filter((c) => IDENTIFIER_FORMATS[c.device_type]),
                ),
            onError,
        );
        meteringTariffApi.list(facility, (data) => setTariffs(data), onError);
        groupApi.list(facility, (data) => setGroups(data), onError);
    }, [facility]);

    const handleAddClick = () => {
        setEditPopUpData({
            facility: Number(facility.split("/").slice(-2)[0]),
            groups: [],
        });
        setEditPopUpOpen(true);
    };

    const handleEditClick = (id) => {
        setEditPopUpData(rows.find((item) => item.id === id));
        setEditPopUpOpen(true);
    };

    const handleEditPopUpClosedWithUpdate = (saved) => {
        if (rows.find((item) => item.id === saved.id))
            setRows(rows.map((row) => (row.id === saved.id ? saved : row)));
        else setRows((oldRows) => [saved, ...oldRows]);
        setEditPopUpOpen(false);
    };

    const handleDeleteClick = (id) => {
        setDeleteCandidate(id);
        setDeleteConfirmOpen(true);
    };

    const handleDelete = (id) => {
        // The API deactivates a point with readings rather than deleting it.
        // Either way it leaves the list, which only has active points.
        powerSensorPointApi.delete(
            `${powerSensorPointApi.baseUrl}${id}/`,
            () => {
                setRows(rows.filter((row) => row.id !== id));
            },
            () => {
                setAlertMessage(t("The point couldn't be deleted."));
                setAlertOpen(true);
            },
        );
        setDeleteCandidate(null);
    };

    const typeOrder = Object.keys(GROUP_TYPE_TO_TEXT);

    const columns = [
        { field: "name", headerName: t("Name"), flex: 1, minWidth: 150 },
        {
            field: "controller",
            headerName: t("Controller"),
            flex: 0.7,
            minWidth: 120,
            valueGetter: ({ value }) =>
                controllers.find((c) => controllerId(c) === value)?.name ?? value,
        },
        {
            field: "identifier",
            headerName: t("Identifier"),
            flex: 0.7,
            minWidth: 120,
        },
        {
            field: "tariff",
            headerName: t("Tariff"),
            flex: 0.7,
            minWidth: 120,
            valueGetter: ({ value }) =>
                tariffs.find((tariff) => tariff.id === value)?.name ?? value,
        },
        {
            field: "groups",
            headerName: t("Groups"),
            flex: 1.5,
            minWidth: 200,
            sortable: false,
            renderCell: ({ value }) => (
                <Stack direction="row" spacing={0.5} sx={{ overflow: "hidden" }}>
                    {groups
                        .filter((group) => value.includes(group.id))
                        .sort(
                            (a, b) =>
                                typeOrder.indexOf(a.type) -
                                    typeOrder.indexOf(b.type) ||
                                a.name.localeCompare(b.name),
                        )
                        .map((group) => (
                            <Chip key={group.id} label={group.name} size="small" />
                        ))}
                </Stack>
            ),
        },
        {
            field: "actions",
            type: "actions",
            headerName: t("Actions"),
            flex: 0.4,
            minWidth: 100,
            cellClassName: "actions",
            getActions: ({ id }) => {
                return [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label={t("Edit")}
                        className="textPrimary"
                        onClick={() => handleEditClick(id)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label={t("Delete")}
                        onClick={() => handleDeleteClick(id)}
                        color="inherit"
                    />,
                ];
            },
        },
    ];

    return (
        <>
            <MeteringPointEditPopUp
                open={editPopUpOpen}
                data={editPopUpData}
                controllers={controllers}
                tariffs={tariffs}
                groups={groups}
                onCloseWithoutSaving={() => setEditPopUpOpen(false)}
                onCloseWithUpdate={handleEditPopUpClosedWithUpdate}
            />
            <Dialog open={alertOpen} onClose={() => setAlertOpen(false)}>
                <DialogTitle id="alert-dialog-title">
                    {t("Problem")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {alertMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAlertOpen(false)} autoFocus>
                        {t("OK")}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
            >
                <DialogTitle id="alert-dialog-title">
                    {t("Confirmation")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {t(
                            "Are you sure you want to delete this point? Its readings are kept.",
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setDeleteConfirmOpen(false);
                            handleDelete(deleteCandidate);
                        }}
                    >
                        {t("YES")}
                    </Button>
                    <Button
                        onClick={() => setDeleteConfirmOpen(false)}
                        autoFocus
                    >
                        {t("NO")}
                    </Button>
                </DialogActions>
            </Dialog>
            <DataGrid
                rows={rows}
                columns={columns}
                components={{
                    Toolbar: PointToolbar,
                }}
                componentsProps={{
                    toolbar: { onAddClick: handleAddClick },
                }}
                getRowId={(row) => row.id}
            />
        </>
    );
}
